// Agent Chat Types - Shared between frontend and backend
// Used for WebSocket events, message types, and status tracking

// ============================================================
// Message Types
// ============================================================

export const MessageType = {
  USER: 'user',
  ASSISTANT: 'assistant',
  TOOL_USE: 'tool_use',
  TOOL_RESULT: 'tool_result',
  TEXT: 'text',
  ERROR: 'error',
  SYSTEM: 'system',
} as const;

export type MessageType = (typeof MessageType)[keyof typeof MessageType];

// ============================================================
// Stream Message Types (Claude SDK protocol)
// ============================================================

export const StreamType = {
  STREAM: 'stream',
} as const;

export type StreamType = (typeof StreamType)[keyof typeof StreamType];

// ============================================================
// Task Status (for streaming/UI state)
// ============================================================

export const TaskStatus = {
  QUEUED: 'queued',
  RUNNING: 'running',
  COMPLETED: 'completed',
  PAUSED: 'paused',
  ERROR: 'error',
} as const;

export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];

// ============================================================
// Tool Status
// ============================================================

export const ToolStatus = {
  RUNNING: 'running',
  COMPLETED: 'completed',
  ERROR: 'error',
} as const;

export type ToolStatus = (typeof ToolStatus)[keyof typeof ToolStatus];

// ============================================================
// Connection State (for WebSocket connection tracking)
// ============================================================

export const ConnectionState = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting',
} as const;

export type ConnectionState = (typeof ConnectionState)[keyof typeof ConnectionState];

// ============================================================
// Socket.io Built-in Events (client-side)
// ============================================================

export const SocketEvent = {
  CONNECT: 'connect',
  DISCONNECT: 'disconnect',
  CONNECT_ERROR: 'connect_error',
  RECONNECT: 'reconnect',
  RECONNECT_ATTEMPT: 'reconnect_attempt',
  RECONNECT_ERROR: 'reconnect_error',
  RECONNECT_FAILED: 'reconnect_failed',
} as const;

export type SocketEvent = (typeof SocketEvent)[keyof typeof SocketEvent];

// ============================================================
// Socket Event Names (Custom Application Events)
// ============================================================

export const AgentSocketEvent = {
  // Client → Server
  START_CLAUDE_SESSION: 'start-claude-session',
  JOIN_SESSION: 'join-session',
  LEAVE_SESSION: 'leave-session',
  STOP_CHAT: 'stop-chat',
  CHECK_HEALTH: 'check-health',
  IMAGE_GEN_REQUEST: 'image-gen-request',
  MEDIA_LIBRARY_SEARCH_REQUEST: 'media-library-search-request',
  MEDIA_LIBRARY_DOWNLOAD_REQUEST: 'media-library-download-request',
  LIST_PROJECT_ASSETS_REQUEST: 'list-project-assets-request',

  // Server → Client
  CONNECTED: 'connected',
  CHAT_RESPONSE: 'chat-response',
  CHAT_ERROR: 'chat-error',
  CHAT_STARTED: 'chat-started',
  CHAT_STOPPED: 'chat-stopped',
  CHAT_COMPLETE: 'chat-complete',
  USER_MESSAGE: 'user-message',
  TASK_CREATED: 'task-created',
  TASK_QUEUED: 'task-queued', // Task queued waiting for container to resume
  TASK_CANCELLED: 'task-cancelled',
  TASK_PAUSED: 'task-paused',
  STREAM_COMPLETE: 'stream-complete',
  TOOL_STARTED: 'tool-started',
  TOOL_COMPLETED: 'tool-completed',
  ASSISTANT_ENTRY_SAVED: 'assistant-entry-saved', // First assistant entry saved to database
  HITL_PERSISTED: 'hitl-persisted',
  HITL_ACTION_CLICKED: 'hitl-action-clicked',
  HITL_REALTIME: 'hitl-realtime',
  AUTO_COMMIT_CREATED: 'auto-commit-created',
  AUTO_COMMIT_FAILED: 'auto-commit-failed',
  SET_VIEWPORT: 'set-viewport', // Agent requests the editor preview switch to a signage/custom viewport
  PREVIEW_TEST_STEP: 'preview-test-step', // Agent ran a Puppeteer preview tool — drives the preview-side Test Window

  SESSION_TITLE_UPDATED: 'session-title-updated',
  HEALTH_STATUS: 'health-status',
  IMAGE_GEN_RESPONSE: 'image-gen-response',
  MEDIA_LIBRARY_SEARCH_RESPONSE: 'media-library-search-response',
  MEDIA_LIBRARY_DOWNLOAD_RESPONSE: 'media-library-download-response',
  LIST_PROJECT_ASSETS_RESPONSE: 'list-project-assets-response',
} as const;

export type AgentSocketEvent = (typeof AgentSocketEvent)[keyof typeof AgentSocketEvent];

// ============================================================
// Ask-User-Question HITL (optidev_ask_user_question)
// ============================================================

/** A single selectable choice within a question. */
export interface AskQuestionOption {
  label: string;
  description: string;
  /** When true, picking this option reveals a text box; the typed value becomes the answer (e.g. a URL). */
  allowsInput?: boolean;
}

/** One question shown in the ask-user-question card (a step / tab). */
export interface AskQuestionItem {
  /** Short label (≈12 chars) — step/tab title and answers-map key. */
  header: string;
  question: string;
  multiSelect: boolean;
  options: AskQuestionOption[];
}

/**
 * Payload carried inside the `<ui_payload>` blob for `hitl_type: 'ask_user_question'`.
 * The UI renders a clean stepper (or a single compact card for one question) and
 * appends a "Something else" free-text choice automatically.
 */
export interface AskUserQuestionPayload {
  hitl_type: 'ask_user_question';
  requestId: string;
  questions: AskQuestionItem[];
}

/** Bounds for ask-user-question answers (mirror the tool schema: ≤4 questions). */
export const ASK_QUESTION_LIMITS = {
  maxQuestions: 4,
  maxValuesPerQuestion: 8,
  maxValueLength: 2000,
  maxHeaderLength: 120,
} as const;

/**
 * Sanitize user-submitted ask-user-question answers before they cross any trust
 * boundary (they flow into the agent's context and, for URLs, into fetch/build
 * steps). Coerces to strings, strips control characters, trims, and caps sizes.
 * Pure + dependency-free so it can run on both client and server.
 */
export function sanitizeAskQuestionAnswers(
  raw: unknown,
): Record<string, string[]> {
  if (!raw || typeof raw !== 'object') return {};
  const clean = (s: unknown, max: number): string =>
    String(s ?? '')
      // Strip ASCII control characters (incl. newlines/tabs) -> single space.
      // eslint-disable-next-line no-control-regex
      .replace(/[\x00-\x1F\x7F]/g, ' ')
      .trim()
      .slice(0, max);

  const out: Record<string, string[]> = {};
  for (const [rawKey, rawVal] of Object.entries(
    raw as Record<string, unknown>,
  ).slice(0, ASK_QUESTION_LIMITS.maxQuestions)) {
    const key = clean(rawKey, ASK_QUESTION_LIMITS.maxHeaderLength);
    if (!key) continue;
    const arr = Array.isArray(rawVal) ? rawVal : [rawVal];
    const values = arr
      .slice(0, ASK_QUESTION_LIMITS.maxValuesPerQuestion)
      .map((v) => clean(v, ASK_QUESTION_LIMITS.maxValueLength))
      .filter((v) => v.length > 0);
    if (values.length) out[key] = values;
  }
  return out;
}

// ============================================================
// Socket Event Payloads
// ============================================================

export interface UserMessagePayload {
  id: string;
  entryId?: string;
  userMessageTempId?: string;
  content: string;
  timestamp: string;
  sessionId: string;
  role: 'user';
}

export interface ChatResponsePayload {
  type: 'stream' | 'error';
  data?: {
    type: 'text' | 'tool_use';
    content?: string;
  };
  sessionId: string;
  taskId: string;
  userMessageId?: string;
  parentEntryId?: string;
}

export interface TaskCreatedPayload {
  taskId: string;
  sessionId: string;
  clientMessageId?: string;
  parentEntryId?: string;
  userMessageId?: string;
}

export interface TaskQueuedPayload {
  taskId: string;
  sessionId: string;
  clientMessageId?: string;
  parentEntryId?: string;
  userMessageId?: string;
  reason: 'container_paused'; // Why the task is queued
}

export interface ChatStartedPayload {
  id: string; // sessionId
  projectId?: string;
}

export interface StreamCompletePayload {
  sessionId: string;
  taskId: string;
  assistantMessageId: string;
  toolUsage?: Record<string, string[]>;
  userMessageId?: string;
  parentEntryId?: string;
  completedAt?: string;
}

export interface ToolStartedPayload {
  sessionId: string;
  taskId: string;
  entryId: string;
  toolUseId: string;
  toolName: string;
  toolInput?: Record<string, unknown>;
  textPosition?: number;
  userMessageId?: string;
  assistantMessageId?: string; // Text entry ID saved before tool (for early ID update)
}

export interface ToolCompletedPayload {
  sessionId: string;
  taskId: string;
  toolUseId: string;
  isError?: boolean;
  resultSummary?: string;
  errorMessage?: string;
  userMessageId?: string;
  completedAt?: string;
  /** Inline screenshot (base64) for capture_url — rendered beneath the tool in chat. */
  imageBase64?: string;
  imageMimeType?: string;
}

export interface AssistantEntrySavedPayload {
  sessionId: string;
  taskId: string;
  entryId: string; // The real database entry ID
  userMessageId?: string;
}

export interface TaskCancelledPayload {
  sessionId: string;
  taskId: string;
  reason?: string;
  timestamp?: string;
}

export interface TaskPausedPayload {
  taskId: string;
  sessionId?: string;
  errorCode?: string;
  errorMessage?: string;
}

export interface HitlPersistedPayload {
  sessionId: string;
  taskId: string;
  messageId: string;
  content: string;
  timestamp: string;
}

export interface HitlActionClickedPayload {
  messageId: string;
  actionId: string;
  sessionId?: string;
}

export interface ChatErrorPayload {
  message: string;
  code?: string;
  error?: string;
}

export interface AutoCommitCreatedPayload {
  sessionId: string;
  commitId: string;
  commitSha: string;
  filesCount: number;
  message: string;
  changedFiles?: string[];
}

/**
 * Preview viewport types the agent can request the editor to switch to —
 * matches the editor's device presets. Signage (`landscape`/`portrait`) targets
 * a fixed physical screen; `mobile`/`tablet`/`desktop` are the general
 * responsive device presets; `custom` carries explicit dimensions; `full` is
 * "Full Size" — responsive mode off, the preview fills the whole pane (no fixed
 * canvas, width/height are ignored).
 */
export type PreviewViewportType =
  | 'mobile'
  | 'tablet'
  | 'desktop'
  | 'landscape'
  | 'portrait'
  | 'custom'
  | 'full';

/**
 * Emitted (SET_VIEWPORT) when the agent decides the preview should snap to a
 * signage/custom canvas — e.g. while building a kiosk, or when the user states
 * exact dimensions. width/height are always fully resolved server-side.
 */
export interface SetViewportPayload {
  sessionId?: string;
  projectId?: string;
  type: PreviewViewportType;
  width: number;
  height: number;
}

// ============================================================
// Preview Test Window (Puppeteer preview tools)
// ============================================================

/**
 * Which Puppeteer-backed preview tool produced a Test Window step. Mirrors the
 * sidecar MCP tools `preview_check` / `preview_screenshot` / `preview_tap` /
 * `preview_inspect` (registered in the dev-container claude-agent).
 */
export type PreviewToolKind = 'check' | 'screenshot' | 'tap' | 'inspect';

/** Lifecycle phase of a Test Window step. */
export type PreviewTestStepPhase = 'started' | 'completed';

/**
 * Outcome of a completed step. `running` is used while a step is in-flight;
 * `issues` means the tool found problems (e.g. preview_check punch-list);
 * `passed` means clean; `error` means the tool itself failed.
 */
export type PreviewTestStepStatus = 'running' | 'passed' | 'issues' | 'error';

/** A single category bucket from a `preview_check` punch-list. */
export interface PreviewTestIssue {
  category: string;
  count: number;
}

/** A single tap in a `preview_tap` sequence trace. */
export interface PreviewTestTapStep {
  label: string;
  ok: boolean;
  detail?: string;
}

/**
 * Emitted (PREVIEW_TEST_STEP) when the agent runs a preview tool. Sent twice
 * per tool call: once with phase `started` (so the step appears instantly with
 * its route/orientation) and once with phase `completed` (carrying the captured
 * screenshot, issue counts, and/or tap trace). The frontend merges the two by
 * `toolUseId` to drive the preview-side Test Window. Screenshots are the
 * already-bounded (≤1568px) JPEGs the tools capture; oversized payloads set
 * `screenshotOmitted` instead of inlining the image.
 */
/**
 * A reconstructed (historical) test step, rebuilt from persisted session
 * entries so the Test Window survives across browsers/devices. Mirrors the live
 * step minus the screenshot bytes (those aren't persisted — `screenshotCaptured`
 * lets the UI show "Screenshot captured" instead).
 */
export interface PreviewTestStepSnapshot {
  toolUseId: string;
  kind: PreviewToolKind;
  status: PreviewTestStepStatus;
  label: string;
  route?: string;
  orientation?: string;
  screenshotCaptured?: boolean;
  issueCount?: number;
  issues?: PreviewTestIssue[];
  tapSteps?: PreviewTestTapStep[];
  summary?: string;
}

/** A reconstructed run (one agent turn) for the Test Window history. */
export interface PreviewTestRunSnapshot {
  taskId: string;
  startedAt: number; // epoch ms
  steps: PreviewTestStepSnapshot[];
}

export interface PreviewTestStepPayload {
  sessionId: string;
  taskId: string;
  /**
   * Owning project. Lets the frontend drop steps belonging to another project:
   * the singleton socket can stay joined to a previous project's session room
   * after in-tab navigation, and the Test Window store is a single
   * project-scoped bucket. Optional so legacy events still ingest.
   */
  projectId?: string;
  toolUseId: string;
  kind: PreviewToolKind;
  phase: PreviewTestStepPhase;
  status: PreviewTestStepStatus;
  /** Short human label, e.g. "Checking page", "Capturing screenshot". */
  label: string;
  route?: string;
  orientation?: string;
  /** Base64 JPEG (no data: prefix). Present on completed screenshot/tap steps. */
  screenshotBase64?: string;
  screenshotMimeType?: string;
  /** True when a screenshot existed but was dropped from the event for size. */
  screenshotOmitted?: boolean;
  issueCount?: number;
  issues?: PreviewTestIssue[];
  tapSteps?: PreviewTestTapStep[];
  /** Free-form one-liner summarising the result. */
  summary?: string;
  isError?: boolean;
  timestamp?: string;
}
